import { utils } from "shared/intrasyncUtilsShim";

import {
  makeObservable,
  computed,
  action,
  observable,
  runInAction,
  when,
} from "mobx";

import { AuthStore } from "@intrasync/stores";
import { RouterStore, startRouter } from "@intrasync/stores";
import ServiceStore from "@intrasync/stores/core/ServiceStore";
import type { ServiceStoreConfig } from "@intrasync/stores";
import { NotificationStore } from "@intrasync/stores";
import ViewStore from "@intrasync/stores/core/ViewStore";
import type { ViewStoreOptions } from "@intrasync/stores/core/ViewStore";
import { ChannelStore } from "@intrasync/stores";
import { ConnectionStore } from "@intrasync/stores";
import { RequestQueueStore } from "@intrasync/stores";
import { RegistrationStore } from "@intrasync/stores";
import { AppLayoutStore } from "@intrasync/stores";
import { DiagnosticsSink } from "@intrasync/stores";
import type { DiagnosticsOptions } from "@intrasync/stores";
import type { LayoutOptions } from "@intrasync/stores/specialized/AppLayoutStore";
import type { RootStoreStorage } from "@intrasync/stores";
// The opt-in (`features/*` + `offline/PWAStore`) classes, taken from the ONE
// module that names them. That module also exports them as the
// `FeatureStoreRegistry` map the library RootStore takes via its `featureStores`
// prop, so this composition root and the registration the library will need
// cannot drift apart. See stores/featureStores.ts.
import {
  BarcodeCameraStore,
  ConversationStore,
  DataGridStore,
  PWAStore,
  PdfEditorStore,
  ScheduleGridStore,
  SignaturePadStore,
  WebApiStore,
} from "./featureStores";
import { ThemeStore } from "@intrasync/stores";
import {
  builtInServices,
  builtInServiceStores,
} from "@intrasync/stores/infrastructure";

import type { Application } from "@feathersjs/feathers";
import serviceStoresConfig from "../serviceStoresConfig";
// Library GanttStore types: the app uses a standalone ganttStore singleton, but
// these fields keep this composition root structurally aligned with the library
// RootStore that the shimmed (library-backed) stores are typed against.
import type { GanttStore, GanttStoreOptions } from "@intrasync/stores/features";
import type { FindAllStore } from "@intrasync/stores/features";
import type RootStoreBase from "@intrasync/stores/core/RootStore";
// `RouterStoreProps` is NOT re-exported from the `@intrasync/stores` barrel —
// the deep specialized path is the only way in (legal via the package's
// `"./specialized/*"` exports wildcard).
import type { RouterStoreProps } from "@intrasync/stores/specialized/RouterStore/RouterStore";
import { isSocketSuspended, resumeSocket, suspendSocket } from "../api-client/socketLink";
import { installRefusedTimeoutLabels, watchConnectRefusals } from "../realtime/connectRefusal";
import { createPageAccessRouteGuard } from "./pageAccessRouteGuard";
import {
  ignoreSessionEndedAuthErrors,
  installSessionGuard,
  tokenSubject,
  watchSessionEnds,
  watchTokensCleared,
  type ReplayVerdict,
  type SessionEndCause,
} from "./sessionGuard";
import {
  OfflineReplayTracker,
  pruneRetainedMirror,
  type QueuedOperation,
  type ReplayableQueue,
} from "./offlineReplay";
import { Theme } from "@mui/material/styles";
import { ServiceStoreManager } from "@intrasync/stores";
import { GlobalReactionOptimizer, MemoryMonitor } from "@intrasync/stores";
import { StoreRegistry } from "@intrasync/stores";
import {
  OfflineQueueManager,
  attemptRetiredOfflineStores,
  sweepOrphanedOfflineMirrors,
} from "@intrasync/stores/offline";
import { BrowserAPIStore } from "@intrasync/stores/offline";
import { PerformanceStore } from "@intrasync/stores";

declare const window: Window;

/**
 * How long purgeUserData waits for the offline queue to finish restoring from
 * disk before it decides which stores are safe to clear. The library
 * RootStore's OFFLINE_QUEUE_RESTORE_WAIT_MS (31.0.2), which it does not export.
 */
const OFFLINE_QUEUE_RESTORE_WAIT_MS = 3000;

/** Slack on top of that wait before a torn-down connection comes back without its purge. */
const RECONNECT_SLACK_MS = 2000;

/**
 * Default bound on the pre-logout replay (flushOfflineQueue). The library
 * RootStore's OFFLINE_LOGOUT_FLUSH_MS (31.0.2), which it does not export.
 */
const OFFLINE_LOGOUT_FLUSH_MS = 5000;

/** The registry key HQ gives a configured service ("sales-orders" -> "salesOrdersStore"). */
const storeKeyFor = (servicePath: string) => `${utils.uriToCamel(servicePath)}Store`;

/** What purgeUserData reports — the library RootStore's result, plus the
 *  HQ view stores it reset (the library has no view-store half). */
export type PurgeUserDataResult = Awaited<
  ReturnType<RootStoreBase["purgeUserData"]>
> & { viewStoresReset: string[] };

/** The parts of a ServiceStore (online or offline-capable) the purge and the
 *  pre-logout replay read. */
type PurgeableServiceStore = {
  name?: string;
  hasPendingSync?: boolean;
  pendingSyncCount?: number;
  isEffectivelyOffline?: boolean;
  syncWithServer?: () => Promise<unknown>;
  clearEntities?: (options?: { clearStorage?: boolean }) => void;
};

type Mode =
  | "dev"
  | "development"
  | "stage"
  | "beta"
  | "live"
  | "prod"
  | "production"
  | "test";

export type RootStoreProps = {
  appName: string;
  appTitle?: string;
  storageName?: string;
  storage?: RootStoreStorage;
  apiOptions?: unknown;
  /** TYPED ViewStore configuration (auth/Autodesk redirect endpoints). Host
   *  endpoint tables belong in `rootStoreConfig.ts`, not in this file. */
  viewStoreOptions?: ViewStoreOptions;
  viewStoreProps?: unknown;
  mode: Mode;
  appVersion?: string;
  autoUpdate?: boolean;
  syncChannels?: boolean;
  /** Typed since 26.8.x — the ServiceStoreManager takes exactly this shape, so
   *  the cast that used to sit at its construction site is gone. HQ's own
   *  ServiceViewConfig is richer than the library's carrier type, so the single
   *  remaining cast lives at the configuration boundary in rootStoreConfig.ts. */
  serviceStores?: ServiceStoreConfig[];
  appStores?: unknown[];
  asyncStorage?: boolean;
  enableRouterStore?: boolean;
  routerStoreProps?: RouterStoreProps;
  enableNotificationStore?: boolean;
  enableAuthStore?: unknown;
  disableLogsStore?: boolean;
  enableRequestQueue?: boolean;
  disableViewStore?: boolean;
  enableWebApiStore?: unknown;
  enableBarcodeCameraStore?: boolean | unknown;
  enableDataGridStore?: boolean;
  enableScheduleGridStore?: boolean;
  dataGridStore?: DataGridStore;
  scheduleGridStore?: ScheduleGridStore;
  /** The constructed FeathersJS client. REQUIRED — built by `createRootStore`,
   *  matching the library RootStore, whose `props.app` is mandatory and whose
   *  `apiOptions` has never built a client. */
  app: Application;
  routerStore?: RouterStore | undefined;
  requestQueueStore?: RequestQueueStore;
  notificationStore?: NotificationStore;
  channelStore?: ChannelStore;
  connectionStore?: ConnectionStore;
  authStore?: AuthStore | undefined;
  viewStore?: ViewStore;
  registrationStore?: RegistrationStore;
  appLayoutStore?: LayoutOptions;
  webApiStore?: WebApiStore;
  barcodeCameraStore?: BarcodeCameraStore;
  channelStoreDebugMode?: boolean;
  customTheme?: unknown;
  /** Sink configuration, forwarded verbatim to `new DiagnosticsSink(...)` —
   *  the same prop the library RootStore takes, so adopting it changes
   *  nothing here. Supplied by rootStoreConfig.ts. */
  diagnostics?: DiagnosticsOptions;
};

class RootStore {
  // [x: string]: unknown;
  app;
  debugMode = false;
  setupComplete = false;
  channelServices: string[] = [];
  apiOptions = {
    debugMode: false,
    uriOverride: {
      development: "",
      stage: "",
      production: "",
    },
    enableFindOne: true,
  };

  appName: string;
  appVersion: string;
  autoUpdate: boolean;
  appTitle: string;
  syncChannels: boolean;
  authStorageKey: string;
  refreshStorageKey: string;
  mode: Mode;
  storage: RootStoreStorage | null;
  viewStoreProps: Record<string, unknown>;
  serviceStores: string[] = [];
  /**
   * Store keys whose data survives purgeUserData (from
   * ServiceStoreConfig.retainOnLogout) — the library RootStore's field.
   */
  retainOnLogoutStoreKeys: string[] = [];
  appStores: string[] = [];
  storageName: string = "";
  asyncStorage: boolean;
  enableRouterStore: boolean = false;
  enableNotificationStore: boolean = false;
  enableAuthStore: boolean = false;
  disableLogsStore: boolean = false;
  enableRequestQueue: boolean = false;
  disableViewStore: boolean = false;
  enableWebApiStore: boolean = false;
  enableBarcodeCameraStore: boolean = false;
  enableDataGridStore: boolean = false;
  enableScheduleGridStore: boolean = false;
  enableGanttStore: boolean | GanttStoreOptions = false;
  dataGridStore: DataGridStore | null = null;
  scheduleGridStore: ScheduleGridStore | null = null;
  ganttStore: GanttStore | null = null;
  routerStore?: RouterStore;
  authStore?: AuthStore;
  viewStore: ViewStore | null = null;
  connectionStore: ConnectionStore | null = null;
  channelStore: ChannelStore | null = null;
  notificationStore: NotificationStore | null = null;
  requestQueueStore: RequestQueueStore | null = null;
  registrationStore: RegistrationStore | null = null;
  appLayoutStore: AppLayoutStore | null = null;
  webApiStore: WebApiStore | null = null;
  barcodeCameraStore: BarcodeCameraStore | null = null;
  /** Declared for structural alignment with the library RootStore only. HQ does
   *  not enable it — see the `enableFindAllStore` removal note below. */
  findAllStore: FindAllStore | null = null;
  userStore: ServiceStore | null = null;
  authRedirectStore: ServiceStore | null = null;
  themeStore!: ThemeStore;
  serviceStoreManager: ServiceStoreManager | null = null;
  reactionOptimizer: GlobalReactionOptimizer | null = null;
  memoryMonitor: MemoryMonitor | null = null;
  storeRegistry: StoreRegistry;
  /**
   * Structured diagnostics sink — the field whose ABSENCE took HQ down.
   *
   * Since 26.8.0 the library dereferences `rootStore.diagnostics.*` UNGUARDED
   * from ~150 sites it owns: `StoreRegistry.warnDuplicate` (which fires on the
   * very first duplicate store key, inside this constructor),
   * `ServiceStoreManager`, and ~28 sites in `AuthStore` (including the failed
   * -login and NotAuthenticated paths). This fork re-implements the composition
   * root, so it never picked the field up when the library added it — and on
   * 26.8.3 the constructor threw `Cannot read properties of undefined (reading
   * 'warn')`, React never mounted, and every gate (tsc, vitest, lint, build)
   * stayed green over a blank page.
   *
   * Built from `props.diagnostics` — the same prop name and shape the library
   * RootStore takes, so this is one less divergence to unpick when the fork
   * goes. It is ASSIGNED AS THE FIRST STATEMENT OF THE CONSTRUCTOR (not as a
   * field initializer) because `new StoreRegistry(...)` on the next line can
   * already emit into it. Omitting the prop yields the previous behaviour: a
   * no-op sink that still console-mirrors `warn`/`error`.
   *
   * HQ supplies `{ level: "info", console: false, onEvent }` from
   * rootStoreConfig.ts — see src/diagnostics/.
   */
  readonly diagnostics: DiagnosticsSink;
  offlineQueueManager: OfflineQueueManager | null = null;
  /**
   * Bumped by purgeUserData. A read (find/get) issued under one value and
   * answered under another is dropped by the session guard (sessionGuard.ts),
   * so a response landing after sign-out cannot refill a purged store.
   */
  sessionEpoch = 0;

  /** Who a queued offline write belongs to: the signed-in user's id, or none. */
  offlinePrincipal(): string | undefined {
    const id = this.authStore?.userId;
    return id == null ? undefined : String(id);
  }

  /**
   * Bumped, synchronously, when a sign-out STARTS — whichever way it starts:
   * the local logout (flushOfflineQueue, the first thing `AuthStore.logout()`
   * calls; the socket's `removeAuthTokens` and a removed user go through it
   * too), the app's `logout` event (a server-initiated sign-out), another tab
   * signing out, and any teardown that clears the AuthStore's tokens
   * (sessionEnding, below) — and again when it purges. The session guard
   * rejects a sign-in / token refresh (`authentication.create`,
   * `refresh-token.create`) that settles under a different value than it
   * started under, so a refresh begun by the person signing out cannot hand
   * their rotated tokens and user to the next person. Separate from
   * `sessionEpoch` so the pre-logout replay and the revoke (which run after the
   * first bump) are not disturbed.
   */
  authEpoch = 0;

  /**
   * A session is ending (sessionGuard.ts `watchSessionEnds` /
   * `watchTokensCleared`, installed around the AuthStore's construction).
   * Starts a new auth epoch at once. Another tab's sign-out also purges this
   * tab's memory: the library only clears the AuthStore there, which left the
   * previous person's rows in every store for whoever signs in next in this
   * tab. Memory only — the tab that signed out cleared the shared offline
   * mirrors itself.
   */
  sessionEnding(cause: SessionEndCause): void {
    if (cause === "peer-sign-out") {
      // The library only clears its AuthStore for this; the Feathers client
      // stays `authenticated` as the person who left, so the connection is
      // torn down here too (see endConnection), now: a sign-out another tab
      // made has nothing to wait for, even while this tab's own logout runs.
      this.endConnection();
      // purgeUserData bumps both epochs before its first await.
      void this.purgeUserData({ includeOffline: false });
      return;
    }
    if (cause === "foreign-credential") {
      // Another tab stored a token that is not this tab's person's, and the
      // watcher kept the library from adopting it. End this tab's session the
      // way the library ends one for a peer sign-out — in memory, leaving
      // shared storage to the tab that wrote it — and purge, so whoever signs
      // in here next starts clean.
      this.diagnostics.warn(
        "RootStore",
        "auth.session_cleared",
        "Another tab stored a credential that is not the signed-in person's; this tab's session was ended instead of adopting it"
      );
      this.endLocalSession();
      void this.purgeUserData({ includeOffline: false });
      return;
    }
    // "logout-event" and "tokens-cleared": the library runs its own teardown
    // and purge; this starts the new epoch and takes the connection down -
    // after this tab's own logout, when one is running (it still needs the
    // connection for its remove call).
    this.authEpoch += 1;
    this.endConnection({ waitForLocalLogout: true });
  }

  /**
   * Who this tab has signed in: the AuthStore's user id, else the subject of
   * the tokens it holds (a session restored from storage before its user
   * loaded), else undefined — nobody, so a sibling's token cannot be mixed
   * into anything (sessionGuard.ts `watchSessionEnds`).
   */
  sessionOwner(): string | undefined {
    const auth = this.authStore as
      | { userId?: unknown; refreshToken?: unknown; token?: unknown }
      | null
      | undefined;
    if (!auth) return undefined;
    if (auth.userId != null) return String(auth.userId);
    return tokenSubject(auth.refreshToken) ?? tokenSubject(auth.token);
  }

  /**
   * The refresh token in the storage every tab shares: the token, `null` when
   * the storage was READ and holds none (another tab signed out), `undefined`
   * when it could not be read (no window, no storage, it threw). The session
   * guard compares it before and after a refresh and treats the last two
   * differently.
   */
  sharedRefreshToken(): string | null | undefined {
    try {
      return typeof window === "undefined"
        ? undefined
        : window.localStorage
          ? window.localStorage.getItem(this.refreshStorageKey) ?? null
          : undefined;
    } catch {
      return undefined;
    }
  }

  /** The access token in the shared storage; same three states as {@link sharedRefreshToken}. */
  sharedAccessToken(): string | null | undefined {
    try {
      return typeof window === "undefined"
        ? undefined
        : window.localStorage
          ? window.localStorage.getItem(this.authStorageKey) ?? null
          : undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Bring the connection back after endConnection tore it down. Anonymous:
   * nothing signs in over it but this tab's own next authenticate. Called when
   * the purge has finished (purgeUserData, or the fallback timer), by the
   * session guard when a call that is the next session's goes out, and by the
   * storage watcher when this signed-out tab takes up the origin's session.
   * A no-op unless the connection is torn down.
   */
  resumeConnection(): void {
    this.reconnectArmed = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.connectionWatchDisposed) return;
    resumeSocket(this.app);
  }

  /** A purge has finished and the connection should come back (see armReconnect). */
  private reconnectArmed = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  /** Purges finished, ever; lets a deferred teardown tell whether its purge already ran. */
  private purgesFinished = 0;
  /** The local logout a teardown is waiting out (see endConnection). */
  private connectionTeardownWaitsOn: Promise<unknown> | null = null;
  /** The local logout that was running when the connection was dropped at once for another cause. */
  private connectionDroppedDuring: Promise<unknown> | null = null;
  /** Set by disposeSessionWatchers: nothing reconnects a disposed store's socket. */
  private connectionWatchDisposed = false;

  /**
   * THE ONE TEARDOWN of this tab's authenticated connection, for every way a
   * session ends: another tab's sign-out, a refused foreign credential, the
   * app's `logout` event, this tab's own logout, and the token-cleared
   * backstop.
   *
   * The Feathers client stays `authenticated` as the person who left, and the
   * server's `logout` acts only on the connection that sent it, so without
   * this the socket kept receiving that person's pushes into the purged stores
   * and would run any call as them; and the next person to sign in on it
   * inherited their server-side channels (api/src/channels.ts now also leaves
   * them at login). {@link suspendSocket} resets the client's authentication,
   * forgets the handshake credentials, empties the offline send buffer and
   * disconnects — without a logout, which would delete the credentials the
   * OTHER tab just stored.
   *
   * The connection then comes back, anonymous, as soon as the purge has
   * finished ({@link armReconnect}): a torn-down socket reads as offline to the
   * router (a page that needs sign-in is queued silently) and to the connection
   * indicator.
   *
   * THIS tab's own logout is the exception to "now", and ONLY it
   * (`waitForLocalLogout`, passed for the library's `logout` event and the
   * tokens-cleared backstop, the two its own teardown raises): it revokes the
   * refresh token, clears the session (that is where the tokens-cleared
   * backstop fires), purges, and only THEN sends `authentication.remove` over
   * the socket. Tearing the socket down at the clear would strand that call
   * (and make the buffered logout sit until its ack timeout), so the teardown
   * waits for the logout to settle. Another tab's sign-out and a refused
   * foreign credential do not wait on it: this tab's person has already left,
   * so the connection is dropped at once, and a wait registered earlier by the
   * logout's own events is cancelled (the drop it asked for has happened).
   *
   * When the wait ends the drop happens only if nobody has signed in here in
   * the meantime ({@link sessionOwner} undefined): a session taken up from a
   * sibling tab while the logout ran owns the connection, and suspending it
   * would leave a signed-in tab on an anonymous socket. The drop comes first and
   * the anonymous resume after it, on every path: the purge's own resume only
   * acts on a connection that is already torn down, so a purge that finishes
   * before the drop (the logout purges before its remove call) cannot bring the
   * connection back early, and the drop then brings it back by itself.
   */
  private endConnection(options: { waitForLocalLogout?: boolean } = {}): void {
    const inFlight = (
      this.authStore as unknown as { logoutInFlight?: Promise<unknown> | null } | null | undefined
    )?.logoutInFlight;
    if (inFlight && options.waitForLocalLogout) {
      // Already waiting on it, or already dropped for another cause meanwhile.
      if (this.connectionTeardownWaitsOn === inFlight || this.connectionDroppedDuring === inFlight) return;
      this.connectionTeardownWaitsOn = inFlight;
      const purgesBefore = this.purgesFinished ?? 0;
      const settled = () => {
        if (this.connectionTeardownWaitsOn !== inFlight) return;
        this.connectionTeardownWaitsOn = null;
        if (this.sessionOwner() !== undefined) return;
        if (!suspendSocket(this.app)) return;
        // The logout's purge ran before its remove call: nothing is left to wait for.
        if ((this.purgesFinished ?? 0) > purgesBefore) this.resumeConnection();
        else this.armReconnect();
      };
      void inFlight.then(settled, settled);
      return;
    }
    // Now. A wait the local logout registered earlier is satisfied by this drop.
    this.connectionTeardownWaitsOn = null;
    this.connectionDroppedDuring = inFlight ?? null;
    if (suspendSocket(this.app)) this.armReconnect();
  }

  /**
   * Reconnect, anonymously, when purgeUserData next finishes. A teardown whose
   * purge never comes (a refresh the server definitively refused clears the
   * session without one) is not left offline: the timer brings it back after
   * the longest a purge waits for the offline queue, plus slack.
   */
  private armReconnect(): void {
    this.reconnectArmed = true;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = setTimeout(
      () => this.resumeConnection(),
      OFFLINE_QUEUE_RESTORE_WAIT_MS + RECONNECT_SLACK_MS
    );
  }

  /**
   * The library's peer-sign-out teardown, for this tab only (no storage
   * writes), plus the connection (see endConnection).
   */
  private endLocalSession(): void {
    this.endConnection();
    const auth = this.authStore as unknown as
      | {
          tokenRefreshTimer?: ReturnType<typeof setTimeout> | null;
          clearStore?: () => void;
          setIsSignedIn?: (value: boolean) => void;
        }
      | null
      | undefined;
    if (!auth) return;
    if (auth.tokenRefreshTimer) {
      clearTimeout(auth.tokenRefreshTimer);
      auth.tokenRefreshTimer = null;
    }
    runInAction(() => auth.clearStore?.());
    auth.setIsSignedIn?.(false);
  }

  /**
   * Removes what the constructor attached to the app, the window and the
   * AuthStore for the session watchers (sessionGuard.ts); run by disposeAll.
   */
  private sessionWatchDisposers: Array<() => void> = [];

  /** Run (once) every disposer the session watchers left; disposeAll calls it. */
  disposeSessionWatchers(): void {
    this.connectionWatchDisposed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    for (const dispose of this.sessionWatchDisposers?.splice(0) ?? []) {
      try {
        dispose();
      } catch (error) {
        console.error("Error removing a session watcher:", error);
      }
    }
  }

  /** The offline queue's replay classifier (offlineReplay.ts). */
  private offlineReplayTracker?: OfflineReplayTracker;
  offlineReplays(): OfflineReplayTracker {
    this.offlineReplayTracker ??= new OfflineReplayTracker(
      () => this.offlinePrincipal(),
      (message, error) =>
        this.diagnostics.error("RootStore", "offline.unavailable", message, { error })
    );
    return this.offlineReplayTracker;
  }

  /**
   * Why offline replay is switched off on this page (the replay safety could
   * not attach to this @intrasync/stores — offlineReplay.ts), or null. Queued
   * writes are kept, not sent, while it is set.
   */
  get offlineReplayDisabled(): string | null {
    return this.offlineReplays().replayDisabled;
  }

  private offlineReplayWarned = false;
  /** Stops watching the socket for HQ's connect refusals (connectRefusal.ts). */
  private stopConnectRefusals: (() => void) | null = null;
  /**
   * Tell the person, once per page, that offline replay is switched off: their
   * offline changes are kept on this device but this build will not send them.
   * The diagnostic alone reached nobody who could act on it.
   */
  warnIfOfflineReplayDisabled(): void {
    if (!this.offlineReplayDisabled || this.offlineReplayWarned) return;
    const notify = (
      this.notificationStore as { addWarningNotification?: (message: string) => unknown } | null
    )?.addWarningNotification;
    if (typeof notify !== "function") return;
    this.offlineReplayWarned = true;
    notify(
      "Offline changes are kept on this device but not sent: this version of HQ cannot send them safely. Reload after the next update to send them."
    );
  }

  /**
   * Is a service call the offline queue replaying a stored write
   * (`OfflineServiceStore.syncWithServer`, also the pre-logout flush)? The
   * session guard lets such a call through — the write is already owned by the
   * queue, and rejecting it would be charged as a failed attempt and sent
   * again — but ONLY when it is exactly the call of the op the queue is
   * executing right now, and that op belongs to the person signed in at this
   * moment. `"refuse"`: it is a replay of someone else's (or an unowned) write;
   * it is not sent and the queue keeps it. Details: offlineReplay.ts.
   */
  isOfflineReplay(call: {
    path: string;
    method: string;
    id?: unknown;
    data?: unknown;
  }): ReplayVerdict {
    return this.offlineReplays().classify(call);
  }

  /**
   * Release the offline writes that were queued with no recorded owner (before
   * owner stamping, or while signed out) and are held back. Call it, signed in
   * as the person whose writes they are (`rootStore.adoptQuarantinedOfflineWrites()`
   * from the browser console), and they replay under that identity on the next
   * sync. Returns how many were released.
   */
  adoptQuarantinedOfflineWrites(): Promise<number> {
    return this.offlineReplays().adoptQuarantined();
  }

  /**
   * View stores created lazily, outside appStoresConfig (a feature that owns no
   * composition-root entry, e.g. the AI agent chat), registered through
   * registerSessionStore so purgeUserData resets them too.
   */
  sessionStores = new Map<string, unknown>();
  pwaStore: PWAStore | null = null;
  browserAPIStore: BrowserAPIStore | null = null;
  performanceStore: PerformanceStore | null = null;
  pdfEditorStore: PdfEditorStore | null = null;
  [key: string]: unknown;

  staticProps = {
    appName: "",
    appVersion: "",
    autoUpdate: false,
    appTitle: "",
    syncChannels: false,
    authStorageKey: "",
    refreshStorageKey: "",
    mode: "",
    storage: "",
    viewStoreProps: {},
    serviceStores: [],
    appStores: [],
    storageName: "",
    asyncStorage: false,
    enableRouterStore: false,
    enableNotificationStore: false,
    enableAuthStore: false,
    disableLogsStore: false,
    enableRequestQueue: false,
    disableViewStore: false,
    enableWebApiStore: false,
    enableBarcodeCameraStore: false,
    enableDataGridStore: false,
    enableScheduleGridStore: false,
    apiOptions: {},
    channelStoreDebugMode: false,
  };
  userPermissionsStore: ServiceStore | null = null;
  logStore: ServiceStore | null = null;

  /**
   * Library-backed stores type their `rootStore` constructor param as the library
   * RootStore. This class is structurally compatible at runtime, so expose a typed
   * view for constructing them. Local stores still take `this`.
   *
   * WHY A CAST AND NOT A BETTER TYPE (checked 2026-07-30, by compiling it):
   * this comment used to claim the divergence was "only local type aliases (e.g.
   * RouteParamsType)". That is wrong — `RouteParamsType` never appears in a single
   * diagnostic. Dropping the cast leaves 16 missing members, and once every public
   * one is supplied via `Pick<>`, the remaining 8 are all `private` on the library
   * class (`routerWhenDispose`, `deferredInitTimeout`, `memoryTestTimeout`,
   * `isDisposed`, `hostCompanyId`, `debug`, `validateStoreKeyCollisions`,
   * `collectServiceStoreNames`). Private members are NOMINAL in TypeScript, so no
   * generic parameter, exported interface, or module augmentation can bridge them.
   *
   * The cast therefore cannot be removed while this fork exists — it goes when the
   * file goes. `class HostRootStore extends LibRootStore {}` is the one shape that
   * compiles without it, and is the intended path off this fork.
   * See Libraries/plan/2026-07-30_rootstore-host-composition-gaps.md.
   */
  private get libRoot(): RootStoreBase {
    return this as unknown as RootStoreBase;
  }

  constructor(props: RootStoreProps) {
    // MUST precede the StoreRegistry: its duplicate-key handler emits into
    // `rootStore.diagnostics` from inside this constructor.
    this.diagnostics = new DiagnosticsSink(props.diagnostics);

    // Initialize store registry first
    this.storeRegistry = new StoreRegistry(this.libRoot);

    makeObservable(this, {
      setupComplete: observable,
      channelServices: observable,
      mountStore: action,
      dismountStore: action,
      setSetupComplete: action,
      setChannelServices: action,
      anyServiceStoreLoading: computed,
      anyServiceStoreHasError: computed,
      storeCount: computed,
      isConnected: computed,
    });

    this.appName =
      props.appName || props.storageName || `@intrasync/${utils.uuid()}`;
    if (!props.storage && !window.localStorage) {
      throw Error("Needs a storage of some sorts");
    }

    if (
      !props.storage &&
      typeof window !== "undefined" &&
      "localStorage" in window
    ) {
      this.storage = window.localStorage;
    } else {
      const isLocalForage = props.storage && !!props.storage.createInstance;
      if (isLocalForage) {
        if (props.storage) {
          this.storage = props.storage.createInstance({ name: this.appName });
        }
      } else {
        this.storage = props.storage;
      }
    }

    this.mode = props.mode || "dev";
    this.appVersion = props.appVersion || "unknown";
    this.autoUpdate = props.autoUpdate || false;
    this.appTitle = props.appTitle || "IntraSync App";
    this.syncChannels = props.syncChannels || props.enableRouterStore || false;
    // The host derives these (rootStoreConfig.ts) and hands them to BOTH the
    // api client and this store, so the token keys the client reads before any
    // store exists can never diverge from the ones AuthStore writes. The
    // fallback keeps the historical derivation for any caller that omits them.
    const hostAuthOptions =
      props.enableAuthStore && typeof props.enableAuthStore === "object"
        ? (props.enableAuthStore as {
            debugMode?: boolean;
            authStorageKey?: string;
            refreshStorageKey?: string;
          })
        : {};
    const storageKeyModeSuffix = ["production", "prod", "live"].includes(
      this.mode
    )
      ? ""
      : `-${this.mode}`;
    this.authStorageKey =
      hostAuthOptions.authStorageKey ?? `feathers-jwt${storageKeyModeSuffix}`;
    this.refreshStorageKey =
      hostAuthOptions.refreshStorageKey ??
      `refresh-token${storageKeyModeSuffix}`;

    let allApiOptions = {
      debugMode: true,
      uriOverride: {
        development: "",
        stage: "",
        production: "",
      },
      enableFindOne: true,
      authStorageKey: this.authStorageKey,
      refreshStorageKey: this.refreshStorageKey,
    };
    if (props.apiOptions && typeof props.apiOptions === "object") {
      allApiOptions = { ...allApiOptions, ...(props.apiOptions as object) };
      runInAction(() => {
        this.apiOptions = allApiOptions;
      });
    }

    // The Feathers client is built by `createRootStore`, not here. The library
    // RootStore requires it via `props.app` and throws without one; a
    // composition root that calls `apiClient()` itself is precisely the coupling
    // that keeps this file a fork.
    if (!props.app) {
      throw Error(
        "Requires app instance to be provided via props.app — a constructed " +
          "FeathersJS client. `apiOptions` does not build one."
      );
    }

    // Event listeners are registered by each ServiceStore via handleEvents()
    // Registering them here as well would cause duplicate event processing.
    // See ServiceStore.ts handleEvents() for the canonical event listener setup.

    this.app = props.app;
    installSessionGuard(
      this.app,
      () => this.sessionEpoch,
      (call) => this.isOfflineReplay(call),
      () => this.authEpoch,
      () => this.sharedRefreshToken(),
      {
        sharedAccessToken: () => this.sharedAccessToken(),
        persistenceUnavailable: () =>
          (this.authStore as { sessionPersistenceUnavailable?: boolean } | null | undefined)
            ?.sessionPersistenceUnavailable === true,
        resumeConnection: () => this.resumeConnection(),
      }
    );
    // A call that times out while HQ refuses this page's connection says so (connectRefusal.ts).
    installRefusedTimeoutLabels(this.app);

    // Initialize OfflineQueueManager for offline support
    // Pass appName to consolidate all offline storage into a single IndexedDB
    //
    // The 3rd/4th arguments are what make the queue OWNER-SCOPED. Without
    // `getPrincipal` the manager stamps no owner on a queued write and replays
    // every op under whoever is signed in when the replay runs — so operator
    // A's offline writes, kept on the device by purgeUserData's skip list,
    // would be sent as operator B (and with B's permissions) after A signs out.
    // With it, an op carries its enqueuer's id; a replay holds ops whose owner
    // is not the signed-in user (or when nobody is signed in) and sends them
    // when that owner signs back in. Read lazily: authStore mounts later and
    // the identity changes on every sign-in and sign-out. Same wiring as the
    // library RootStore (31.0.2).
    this.offlineQueueManager = new OfflineQueueManager(
      this.appName,
      false,
      this.diagnostics,
      () => this.offlinePrincipal()
    );
    // Know which queued op is executing, re-check its owner when its call is
    // made, and hold ops that have no recorded owner (offlineReplay.ts). What
    // comes back is the queue every store reads: the manager itself, or — if
    // this @intrasync/stores can no longer be wrapped — a facade that never
    // replays (fail closed; `offlineReplayDisabled` says why).
    this.offlineQueueManager = this.offlineReplays().attach(
      this.offlineQueueManager as unknown as ReplayableQueue
    ) as unknown as OfflineQueueManager;

    // Initialize PWA Store for progressive web app features
    this.pwaStore = new PWAStore(this.libRoot);

    // Initialize Browser API Store
    this.browserAPIStore = new BrowserAPIStore(this.libRoot);

    // Initialize Performance Store
    this.performanceStore = new PerformanceStore(this.libRoot);

    // Initialize PDF Editor Store for PDF collaboration
    this.pdfEditorStore = new PdfEditorStore(this.libRoot);

    this.mountStore("conversationStore", new ConversationStore(this.libRoot));
    if (props.viewStoreProps && typeof props.viewStoreProps === "object") {
      this.viewStoreProps = { ...(props.viewStoreProps as object) };
    }
    if (props.enableWebApiStore) {
      this.mountStore("webApiStore", new WebApiStore(this));
    }
    if (props.enableBarcodeCameraStore) {
      const barcodeCameraStoreProps =
        typeof props.enableBarcodeCameraStore === "object"
          ? props.enableBarcodeCameraStore
          : {};
      this.mountStore(
        "barcodeCameraStore",
        new BarcodeCameraStore(this.libRoot, barcodeCameraStoreProps)
      );
    }

    this.mountStore(
      "themeStore",
      new ThemeStore(this.libRoot, props.customTheme as Theme | undefined)
    );
    // settingsStore (+ its user-preferences DB sync) is configured in
    // appStoresConfig.ts, alongside the other app stores.
    // ProductStore and ProductViewStore are now configured in appStoresConfig.ts
    //
    // `cadStore` is NOT mounted: this used to mount HQ's own src/stores/CadStore
    // at the library's reserved `cadStore` key, and it had zero readers anywhere
    // in src/ (libraries-adoption item 4a). The local class and its unit test
    // stay; only the unused mount is gone.

    if (props.enableDataGridStore) {
      this.setupDataGridStore();
    }
    if (props.enableScheduleGridStore) {
      this.setupScheduleGridStore();
    }
    // `enableFindAllStore` is gone (libraries-adoption item 4c). It was a BARE
    // FIELD ASSIGNMENT, never `mountStore`d — so the registry never saw the
    // store, `dismountStore`/`storeRegistry.disposeAll` could not reach it, and
    // the `"findAllStore"` entry in getStoreType below was dead. Nothing in
    // src/ or @intrasync/react ever read it.
    if (!props.disableLogsStore) {
      this.mountStore(
        "logStore",
        new ServiceStore(this.libRoot, "log", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
    }
    if (props.enableRequestQueue) {
      this.mountStore("requestQueueStore", new RequestQueueStore(this.libRoot, {}));
    }
    if (props.enableNotificationStore) {
      this.mountStore("notificationStore", new NotificationStore(this.libRoot));
    }
    this.warnIfOfflineReplayDisabled();
    // Initialize ServiceStoreManager for batched store loading
    if (props.serviceStores && props.serviceStores.length > 0) {
      // Initializing ServiceStoreManager
      this.serviceStores = (props.serviceStores as { name: string }[]).map(
        (service: { name: string }) => storeKeyFor(service.name)
      );
      this.retainOnLogoutStoreKeys = props.serviceStores
        .filter((service) => service.retainOnLogout)
        .map((service) => storeKeyFor(service.name));

      // Create ServiceStoreManager with smaller batch size for performance
      this.serviceStoreManager = new ServiceStoreManager(
        this.libRoot,
        props.serviceStores,
        100 // Reduced from 15 to 20 for faster initial batches
      );

      // Load more batches immediately to ensure critical stores are available
      // Only initialize first 2 batches (16 stores) immediately for faster startup
      this.serviceStoreManager
        .initializeEagerBatches(2)
        .catch((error: Error) => {
          console.error(
            "Failed to initialize eager service store batch:",
            error
          );
        });
    }

    // Mount critical stores first (auth-related)
    this.mountStore(
      "refreshTokenStore",
      new ServiceStore(this.libRoot, "refresh-token", {
        enableStorage: false,
        enableErrorNotifications: true,
      })
    );
    this.mountStore(
      "userStore",
      new ServiceStore(this.libRoot, "user", {
        enableStorage: true,
        enableErrorNotifications: true,
      })
    );
    this.mountStore(
      "userPermissionsStore",
      new ServiceStore(this.libRoot, "user-permissions", {
        enableStorage: true,
        enableErrorNotifications: true,
      })
    ).then(() => {
      this.userPermissionsStore?.handleFirstLoad();
    });

    // Defer non-critical stores until after auth setup
    this.deferredStoreInitialization();

    // BEFORE the AuthStore: its constructor registers its own `logout` and
    // `storage` listeners, and these must run first so a new auth epoch starts
    // before the library's teardown awaits anything (sessionGuard.ts).
    this.sessionWatchDisposers.push(
      watchSessionEnds({
        app: this.app,
        target: typeof window !== "undefined" ? window : undefined,
        refreshStorageKey: this.refreshStorageKey,
        authStorageKey: this.authStorageKey,
        sessionOwner: () => this.sessionOwner(),
        onEnd: (cause) => this.sessionEnding(cause),
        onAdopt: () => this.resumeConnection(),
      })
    );
    const authStore = new AuthStore(this.libRoot, {
        debugMode:
          (props.enableAuthStore as { debugMode: boolean }).debugMode || false,
        authStorageKey: this.authStorageKey,
        refreshStorageKey: this.refreshStorageKey,
        idField: "id",
        userService: "user",
        entityField: "user",
        // Sliding sessions: every token refresh also swaps the refresh token,
        // so an active user is never signed out on the seven-day wall. OFF by
        // default in the library because the `rotate` flag is an extra property
        // that a host validating `refresh-token` create with
        // `additionalProperties: false` BEFORE its exchange hook answers with a
        // 400 — which the client reads as a definitive credential rejection.
        // HQ is that shape (validateData runs first), so it admits the property
        // explicitly: `rotate` is declared on `refreshTokenDataSchema`
        // (api/src/services/user/refresh-tokens/refresh-tokens.schema.ts) and
        // consumed by the exchange hook. The two must stay together — dropping
        // it from the schema signs every HQ user out on their next refresh.
        rotateRefreshTokens: true,
      });
    this.sessionWatchDisposers.push(ignoreSessionEndedAuthErrors(authStore));
    const unwatchTokens = watchTokensCleared(authStore, () =>
      this.sessionEnding("tokens-cleared")
    );
    if (unwatchTokens) {
      this.sessionWatchDisposers.push(unwatchTokens);
    } else {
      this.diagnostics.error(
        "RootStore",
        "auth.init_failed",
        "The AuthStore has no observable token to watch; a teardown other than logout, the logout event or another tab's sign-out will not start a new auth epoch"
      );
    }
    this.mountStore("authStore", authStore);

    if (this.syncChannels) {
      const options = {
        debugMode: props.channelStoreDebugMode ?? false,
      };
      this.mountStore("channelStore", new ChannelStore(this.libRoot, options));
    }

    if (!("routes" in (props.routerStoreProps ?? {}))) {
      throw Error("Routes List Required");
    }

    if (props.routerStoreProps) {
      this.mountStore(
        "routerStore",
        new RouterStore(
          this.libRoot,
          Object.values(props.routerStoreProps.routes),
          Object.values(props.routerStoreProps.layouts),
          {
            config: {
              html5history: props.routerStoreProps?.isHashRouter ? false : true,
              defaultRoute: props.routerStoreProps.defaultRoute || "Home",
            },
            appName: this.appName || "Unknown",
            appVersion: props.appVersion || "unknown",
            debugMode: props.routerStoreProps.debugMode || false,
            defaultRoute: props.routerStoreProps.defaultRoute || "Home",
            historyEnabled: true,
            // HQ's per-user page-access deny rules. The library RouterStore has
            // no `pageAccessStore` concept — `routeGuards` is the seam that
            // replaced the forked PermissionManager. Anything the host injects
            // here MUST be appended to, never replaced, or blocked users
            // silently regain access to every page.
            routeGuards: [
              ...(props.routerStoreProps.routeGuards ?? []),
              createPageAccessRouteGuard(this),
            ],
          }
        )
      ).then(() => {
        // Set up router initialization after auth is ready
        this.initializeRouterWhenReady(props);
      });
    }

    this.mountStore(
      "appLayoutStore",
      new AppLayoutStore(this.libRoot, {
        // Forward EVERY layout option from rootStoreConfig.ts (the nav
        // taxonomy, `omnibar`, ...). This used to copy five named fields, which
        // silently dropped any option added later. Only the three required
        // strings need a default.
        ...props?.appLayoutStore,
        toolboxBase: props?.appLayoutStore?.toolboxBase || "",
        rightNavButtonTitle: props?.appLayoutStore?.rightNavButtonTitle || "",
        rightNavDefaultRoute: props?.appLayoutStore?.rightNavDefaultRoute || "",
      } as LayoutOptions)
    );
    // With no sidebar, the OmniBar is HQ's navigation. Settings is a param
    // route (/settings/:page), which page search does not list, so it gets a
    // command (the same goTo as the account menu's Settings item).
    this.appLayoutStore?.registerOmnibarCommands([
      {
        id: "hq.settings",
        title: "Settings",
        subtitle: "Your profile, theme and notifications",
        keywords: ["preferences", "profile", "theme", "dark mode", "notifications", "account"],
        run: () => {
          void this.routerStore?.goTo("Settings");
        },
      },
    ]);

    if (props.appStores && props.appStores.length > 0) {
      this.appStores = (props.appStores as { name: string }[]).map(
        (store: { name: string }) => store.name
      );
      props.appStores.forEach(
        (appStore: {
          name: string;
          store: new (rootStore: RootStore) => unknown;
        }) => {
          this.mountStore(
            (
              appStore as {
                name: string;
                store: new (rootStore: RootStore) => unknown;
              }
            ).name,
            new (
              appStore as {
                name: string;
                store: new (rootStore: RootStore) => unknown;
              }
            ).store(this)
          );
        }
      );
    }

    if (!props.disableViewStore) {
      // HQ's auth-redirect endpoint tables come from rootStoreConfig.ts through
      // the library's typed `viewStoreOptions` seam (libraries-adoption item 3).
      this.mountStore(
        "viewStore",
        new ViewStore(this.libRoot, props.viewStoreOptions ?? {})
      );
    }
    if (this.app && this.mode !== "test") {
      this.mountStore("connectionStore", new ConnectionStore(this.libRoot));
      // HQ refusing the connection (too many reconnects, too many pages) is told in its own words,
      // not as a bare "Server Disconnected" (item #3981).
      const socket = (this.app as unknown as { io?: Parameters<typeof watchConnectRefusals>[0] }).io;
      if (socket) {
        this.stopConnectRefusals = watchConnectRefusals(
          socket,
          (message) => {
            (this.notificationStore as { addWarningNotification?: (m: string) => unknown } | null)?.addWarningNotification?.(message);
          },
          // Never bring back a connection a sign-out tore down (socketLink.ts).
          { mayReconnect: () => !this.connectionWatchDisposed && !isSocketSuspended(this.app) }
        );
      }
    }

    // Initialize reaction optimization after all stores are created
    this.reactionOptimizer = new GlobalReactionOptimizer(this.libRoot);
    this.reactionOptimizer.initialize();

    // Optimize reactions for key stores
    this.reactionOptimizer.optimizeAuthReactions();
    this.reactionOptimizer.optimizeConnectionReactions();
    this.reactionOptimizer.optimizeNotificationReactions();

    // Log optimization results
    if (this.debugMode) {
      console.log(this.reactionOptimizer.getOptimizationReport());
    }

    // Initialize memory monitoring
    this.memoryMonitor = MemoryMonitor.getInstance();

    // Expose store count for memory monitoring
    (window as unknown as { __STORE_COUNT__: number }).__STORE_COUNT__ =
      this.storeCount;

    // Expose memory monitor globally for debugging
    if (this.mode === "development" || this.debugMode) {
      (
        window as unknown as { __MEMORY_MONITOR__: MemoryMonitor | null }
      ).__MEMORY_MONITOR__ = this.memoryMonitor;
    }

    // Start memory monitoring in development mode
    if (this.mode === "development" && this.debugMode) {
      this.memoryMonitor.startMonitoring(60000); // Monitor every 60 seconds (increased from 30)

      // Run automated disposal tests only once
      setTimeout(() => {
        if (this.memoryMonitor && !this.memoryMonitor.hasRunTests) {
          this.memoryMonitor
            .runAutomatedTests(this)
            .then((results: unknown) => {
              console.log("Automated memory tests completed:", results);
              if (this.debugMode) {
                console.log(this.memoryMonitor?.generateReport());
              }
            })
            .catch((error: Error) => {
              console.error("Memory tests failed:", error);
            });
        }
      }, 10000); // Increased delay to 10 seconds
    }

    this.setSetupComplete();
  }

  setChannelServices = (serviceNames: string[]) => {
    this.channelServices = serviceNames;
  };

  setSetupComplete = () => {
    this.setupComplete = true;
    // rootStore is ready
  };

  /**
   * Initialize router when both routes are loaded and auth is ready
   */
  private initializeRouterWhenReady = (props: RootStoreProps) => {
    // Use when() to wait for both conditions
    when(
      // Condition: routes initialized AND auth is not pending
      () => {
        const routesReady = this.routerStore?.routesInitialized === true;
        const authReady = !this.authStore?.isAuthenticatePending;

        // Check router initialization conditions

        // Start router if routes are ready AND (auth is ready OR user is not signed in)
        // This handles both authenticated and non-authenticated routes
        return routesReady && authReady;
      },
      // Action: start the router
      () => {
        // Starting router - all conditions met
        // The library `startRouter` hands its own autorun disposer to
        // `RouterStore.registerDisposer`, so the return value is deliberately
        // discarded here (the fork leaked it).
        startRouter(this.libRoot, {
          defaultRoute: props.routerStoreProps?.defaultRoute || "Home",
          html5history: props.routerStoreProps?.isHashRouter ? false : true,
        });
      }
    );
  };

  /**
   * Initialize non-critical stores after auth setup to improve startup performance
   */
  private deferredStoreInitialization = () => {
    // Use setTimeout to defer initialization until next tick
    setTimeout(() => {
      this.mountStore("signaturePadStore", new SignaturePadStore(this.libRoot));
      this.mountStore(
        "userSubscriptionStore",
        new ServiceStore(this.libRoot, "user-subscription", {
          enableStorage: false,
          enableErrorNotifications: true,
        })
      );
      this.mountStore("registrationStore", new RegistrationStore(this.libRoot));

      // Initialize remaining service store batches
      if (this.serviceStoreManager) {
        this.serviceStoreManager
          .ensureAllBatchesInitialized()
          .catch((error: Error) => {
            console.error(
              "Failed to initialize remaining service store batches:",
              error
            );
          });
      }
    }, 10); // 10ms delay to allow auth initialization to start
  };

  mountStore = (storeName: string, store: unknown, isServiceStore = false) => {
    this.debugMode && console.log("mountStore storeName", storeName);

    let actualStore: unknown;

    if (
      typeof store === "function" &&
      store.prototype &&
      store.prototype.constructor
    ) {
      actualStore = new (store as new (rootStore: RootStore) => unknown)(this);
    } else {
      actualStore = store;
    }

    // Use StoreRegistry for type-safe registration
    if (isServiceStore) {
      this.storeRegistry.registerServiceStore(
        storeName,
        actualStore as ServiceStore,
        (actualStore as ServiceStore).name || storeName
      );
    } else {
      // Determine store type and register appropriately
      const storeType = this.getStoreType(storeName);
      if (storeType === "builtin") {
        this.storeRegistry.registerBuiltinStore(
          storeName,
          actualStore,
          storeType
        );
      } else {
        this.storeRegistry.registerViewStore(storeName, actualStore);
      }
    }

    // Maintain backward compatibility
    this[storeName] = actualStore;

    return Promise.resolve(`${storeName} added to rootStore`);
  };

  /**
   * Helper method to determine store type for registry
   */
  private getStoreType(storeName: string): "builtin" | "view" {
    const builtinStores = [
      "authStore",
      "viewStore",
      "connectionStore",
      "channelStore",
      "notificationStore",
      "requestQueueStore",
      "registrationStore",
      "appLayoutStore",
      "webApiStore",
      "barcodeCameraStore",
      "themeStore",
      "dataGridStore",
      "scheduleGridStore",
      "settingsStore",
      "signaturePadStore",
      "userPermissionsStore",
      "userSubscriptionStore",
      "refreshTokenStore",
      "userStore",
      "logStore",
    ];

    return builtinStores.includes(storeName) ? "builtin" : "view";
  }

  dismountStore = (storeName: string | number) => {
    const storeNameStr = storeName.toString();

    if (!this.storeRegistry.hasStore(storeNameStr)) {
      return console.warn("that store was not mounted");
    }

    // Use StoreRegistry for safe disposal
    const disposed = this.storeRegistry.unregisterStore(storeNameStr);

    if (!disposed) {
      console.warn(`Failed to dispose store: ${storeNameStr}`);
    }

    return Promise.resolve(`${storeName} removed from rootStore`);
  };

  unmountStore = (storeName: string | number) => {
    return this.dismountStore(storeName);
  };

  setupDataGridStore = (specificName?: string) => {
    try {
      if (specificName) {
        this.mountStore(specificName, new DataGridStore(this.libRoot));
        return true;
      }
      this.mountStore("dataGridStore", new DataGridStore(this.libRoot));
      return true;
    } catch (err) {
      return false;
    }
  };

  setupScheduleGridStore = (specificName?: string) => {
    // console.log('setupScheduleGridStore specificName', specificName);
    try {
      if (specificName) {
        this.mountStore(specificName, new ScheduleGridStore(this.libRoot));
        return true;
      }
      this.mountStore("scheduleGridStore", new ScheduleGridStore(this.libRoot));
      return true;
    } catch (err) {
      return false;
    }
  };

  disposeAll = () => {
    console.log("RootStore: Beginning comprehensive disposal...");

    // The session watchers first: a teardown below must not be read as a
    // sign-out (or a sibling's write as a foreign credential) by a page that is
    // going away.
    this.disposeSessionWatchers();

    // Dispose all stores using StoreRegistry (type-safe)
    try {
      if (this.serviceStoreManager) {
        this.serviceStoreManager.disposeAll();
        this.serviceStoreManager = null;
      }

      // Use StoreRegistry for comprehensive disposal
      this.storeRegistry.disposeAll();
    } catch (error) {
      console.error("Error disposing stores:", error);
    }

    // Dispose all app stores
    try {
      (this.appStores as string[])?.forEach((storeName: string) => {
        const store = this.storeRegistry.getStore(storeName);
        if (
          store &&
          typeof (store as Record<string, unknown>).dispose === "function"
        ) {
          (store as { dispose: () => void }).dispose();
        }
      });
    } catch (error) {
      console.error("Error disposing app stores:", error);
    }

    // Dispose built-in stores with error handling
    try {
      this.authStore?.dispose();
      this.userPermissionsStore?.dispose();
      this.viewStore?.dispose();
      this.connectionStore?.dispose();
      this.stopConnectRefusals?.();
      this.stopConnectRefusals = null;

      // Safe disposal for stores that may not have dispose method
      if (this.channelStore && "dispose" in this.channelStore) {
        (this.channelStore as { dispose: () => void }).dispose();
      }
      if (this.notificationStore && "dispose" in this.notificationStore) {
        (this.notificationStore as { dispose: () => void }).dispose();
      }
      if (this.requestQueueStore && "dispose" in this.requestQueueStore) {
        (this.requestQueueStore as { dispose: () => void }).dispose();
      }
      if (this.registrationStore && "dispose" in this.registrationStore) {
        (this.registrationStore as { dispose: () => void }).dispose();
      }
      if (this.appLayoutStore && "dispose" in this.appLayoutStore) {
        (this.appLayoutStore as { dispose: () => void }).dispose();
      }
      if (this.webApiStore && "dispose" in this.webApiStore) {
        (this.webApiStore as { dispose: () => void }).dispose();
      }
      if (this.barcodeCameraStore && "dispose" in this.barcodeCameraStore) {
        (this.barcodeCameraStore as { dispose: () => void }).dispose();
      }
      if (this.themeStore && "dispose" in this.themeStore) {
        (this.themeStore as { dispose: () => void }).dispose();
      }
      if (this.dataGridStore && "dispose" in this.dataGridStore) {
        (this.dataGridStore as { dispose: () => void }).dispose();
      }
      if (this.scheduleGridStore && "dispose" in this.scheduleGridStore) {
        (this.scheduleGridStore as { dispose: () => void }).dispose();
      }
    } catch (error) {
      console.error("Error disposing built-in stores:", error);
    }

    // Remove all service event listeners to prevent memory leaks (fixes 272+ listener accumulation)
    if (this.app) {
      try {
        const services = [
          ...builtInServices,
          ...serviceStoresConfig.map((service: { name: string }) => service.name),
        ];
        services.forEach((serviceName: string) => {
          const service = this.app.service(serviceName);
          if (service?.removeAllListeners) {
            service.removeAllListeners();
          }
        });
      } catch (error) {
        console.error("Error removing service event listeners:", error);
      }
    }

    // Dispose reaction optimizer
    if (this.reactionOptimizer) {
      this.reactionOptimizer.dispose();
      this.reactionOptimizer = null;
    }

    // Stop memory monitoring and generate final report
    if (this.memoryMonitor) {
      if (this.debugMode) {
        console.log(
          "Final Memory Report:",
          this.memoryMonitor.generateReport()
        );
      }
      this.memoryMonitor.stopMonitoring();
      this.memoryMonitor = null;
    }

    // Clear global store count
    if (typeof window !== "undefined") {
      delete (window as unknown as { __STORE_COUNT__?: number }).__STORE_COUNT__;
    }

    console.log("RootStore: Disposal completed");
  };

  /**
   * Have purgeUserData reset a view store this RootStore did not construct —
   * one a feature creates lazily on first use instead of listing it in
   * appStoresConfig. The store needs `reset()` (or `resetForSignOut()`); a
   * second registration under the same name replaces the first.
   */
  registerSessionStore(name: string, store: { reset?: () => void; resetForSignOut?: () => void }) {
    this.sessionStores.set(name, store);
  }

  /**
   * Evict the signed-in user's data on sign-out.
   *
   * The library AuthStore's logout (both the local `logout()` and the server's
   * `logout` event) calls `rootStore.purgeUserData?.({ includeOffline })`. This
   * fork never had the method, so the optional call was a no-op and every
   * ServiceStore kept the previous person's rows — including the one-time
   * secrets a `create()` writes into the cache — for whoever signed in next on
   * the same browser.
   *
   * Modelled on the library RootStore's `purgeUserData` (31.0.2) and returning
   * the same shape, because AuthStore reads `skipped` and `stillQueued` from
   * it. Two HQ differences:
   *
   *  - This fork registers its own built-in ServiceStores (`userStore`,
   *    `userPermissionsStore`, `logStore`, …) in the registry's BUILT-IN tier,
   *    where the library's `instanceof` check would have put them in the
   *    service tier. They are purged too, so the candidate list is the service
   *    tier plus the library's `builtInServiceStores` keys.
   *  - HQ's view stores hold entity arrays of their own. Every app store
   *    exposes `reset()` (or `resetForSignOut()`, preferred where `reset()` is
   *    a narrower route-exit reset), which returns it to its constructed state
   *    without a network call; each is called here and listed in
   *    `viewStoresReset`, as is every view store a feature created lazily and
   *    handed to `registerSessionStore`. `appStoresReset.test.ts` fails for a
   *    store that has no reset, whose reset misses a field, or that the purge
   *    cannot reach at all.
   *  - It starts a new `sessionEpoch` first, so a read still in flight for the
   *    person signing out is dropped when it answers (sessionGuard.ts) instead
   *    of refilling a store this purge just emptied.
   *
   * With `includeOffline` it then empties the offline mirrors no mounted store
   * owns (purgeOrphanedMirrors), as the library purge has done since 31.0.6.
   *
   * Durability beats confidentiality, as in the library: a store holding
   * unsynced offline writes is skipped (unless `force`), and with the offline
   * queue's state unknown only memory is cleared. Never throws.
   */
  async purgeUserData(
    options: { includeOffline?: boolean; force?: boolean } = {}
  ): Promise<PurgeUserDataResult> {
    const cleared: string[] = [];
    const skipped: PurgeUserDataResult["skipped"] = [];
    const failed: PurgeUserDataResult["failed"] = [];
    const mirrorsKept: string[] = [];
    const viewStoresReset: string[] = [];
    /** Service names whose queued writes `skipped` already reports. */
    const accounted = new Set<string>();
    const retained = new Set(this.retainOnLogoutStoreKeys);
    const includeOffline = options.includeOffline ?? false;

    // Every read still in flight belongs to the session that is ending: from
    // here on its response is dropped (see sessionGuard.ts), so it cannot
    // repopulate a store this purge has just emptied.
    this.sessionEpoch += 1;
    this.authEpoch += 1;

    // Never decide on an unloaded queue: until the restore settles every
    // store's hasPendingSync reads false.
    const queueState = await this.awaitOfflineQueueRestore();
    const queueKnown = queueState === "restored" || queueState === "absent";

    for (const name of this.purgeableServiceStoreNames()) {
      if (retained.has(name)) continue;
      const store = this.purgeableServiceStore(name);
      if (!store || typeof store.clearEntities !== "function") continue;
      try {
        if (!options.force && store.hasPendingSync) {
          skipped.push({ store: name, pendingCount: store.pendingSyncCount ?? 1 });
          accounted.add(String(store.name ?? name));
          // The store stays (its unsynced writes reconcile onto its rows), but
          // not the rest of the previous person's cached list.
          pruneRetainedMirror(
            store as unknown as Parameters<typeof pruneRetainedMirror>[0],
            this.queuedOperations()
          );
          continue;
        }
        const keepMirror =
          includeOffline &&
          !queueKnown &&
          !options.force &&
          typeof store.hasPendingSync === "boolean";
        store.clearEntities({ clearStorage: includeOffline && !keepMirror });
        cleared.push(name);
        if (keepMirror) mirrorsKept.push(name);
      } catch (error) {
        failed.push({ store: name, error: error as Error });
      }
    }

    const resettable: Array<[string, unknown]> = [
      ...this.appStores.map(
        (name): [string, unknown] => [name, this.storeRegistry.getStore(name) ?? this[name]]
      ),
      ...this.sessionStores,
    ];
    for (const [name, candidate] of resettable) {
      const store = candidate as
        | { reset?: () => void; resetForSignOut?: () => void }
        | null
        | undefined;
      // `resetForSignOut()` wins where a store has both: its `reset()` is then
      // a narrower route-exit reset (a Data Mapper wizard step keeps its draft
      // across route changes, and must not keep it across people).
      const reset =
        typeof store?.resetForSignOut === "function"
          ? store.resetForSignOut
          : store?.reset;
      if (!store || typeof reset !== "function") continue;
      try {
        reset.call(store);
        viewStoresReset.push(name);
      } catch (error) {
        failed.push({ store: name, error: error as Error });
      }
    }

    // The OmniBar's personal state (favorites, recent pages, frecency, typed
    // searches) lives in localStorage. The user's favorites are safe on the
    // server (settingsSync), so wipe every user's copy from this browser.
    try {
      this.appLayoutStore?.resetPersonalState({ allUsers: true });
    } catch (error) {
      failed.push({ store: "appLayoutStore", error: error as Error });
    }

    const orphanMirrorsCleared = includeOffline
      ? await this.purgeOrphanedMirrors({
          retained,
          queueKnown,
          force: options.force ?? false,
          mirrorsKept,
          failed,
        })
      : [];

    if (failed.length) {
      this.diagnostics.error(
        "RootStore",
        "root.purge_failed",
        `purgeUserData: ${failed.length} store(s) failed to clear`,
        { data: { stores: failed.map((f) => f.store) } }
      );
    }
    if (mirrorsKept.length) {
      this.diagnostics.warn(
        "RootStore",
        "root.purge_mirrors_kept",
        `purgeUserData: the offline queue ${
          queueState === "failed" ? "failed to restore" : "had not restored in time"
        }, so ${mirrorsKept.length} offline mirror(s) were kept on disk (memory was cleared)`,
        { data: { stores: mirrorsKept, queueState } }
      );
    }
    const stillQueued = this.queuedOutside(accounted);
    // The previous person's data is gone: a connection torn down for this
    // sign-out comes back now, anonymous (see endConnection).
    this.purgesFinished = (this.purgesFinished ?? 0) + 1;
    if (this.reconnectArmed) this.resumeConnection();
    return {
      cleared,
      skipped,
      failed,
      mirrorsKept,
      stillQueued,
      orphanMirrorsCleared,
      viewStoresReset,
    };
  }

  /**
   * purgeUserData's sweep of the offline mirrors (`offline-<service>` in this
   * app's IndexedDB) that no store mounted in this session owns: a store that
   * was offline-capable in an earlier release and is online-only now, or one
   * this session has not mounted. The per-store pass cannot reach them, so they
   * kept the previous person's rows.
   *
   * The library RootStore's `purgeOrphanedMirrors` (31.3.x), over the fork's
   * store set, with its keep rules: a mirror stays when a mounted store owns it,
   * its store is `retainOnLogout`, a queued write may replay against it, or the
   * queue's state is unknown (then it is reported in `mirrorsKept` as
   * `offline-<service>`). `force` keeps only owned and retained mirrors. Rows
   * are cleared; the store is not dropped. Appends to mirrorsKept and failed;
   * never throws. Returns the services whose mirror was emptied.
   */
  private async purgeOrphanedMirrors(ctx: {
    retained: Set<string>;
    queueKnown: boolean;
    force: boolean;
    mirrorsKept: string[];
    failed: PurgeUserDataResult["failed"];
  }): Promise<string[]> {
    if (!this.appName) return [];

    // The per-store pass handled these: mounted offline stores (hasPendingSync).
    const owned = new Set<string>();
    for (const key of this.purgeableServiceStoreNames()) {
      const store = this.purgeableServiceStore(key);
      if (store?.name && typeof store.hasPendingSync === "boolean") {
        owned.add(String(store.name));
      }
    }
    const queued = new Set(this.queuedOperations().map((op) => op.storeName));

    const unknownQueue: string[] = [];
    const sweep = await sweepOrphanedOfflineMirrors(this.appName, (service) => {
      if (owned.has(service)) return "owned";
      if (ctx.retained.has(storeKeyFor(service))) return "retained";
      if (ctx.force) return null;
      // A queued write may replay against this mirror once its store mounts.
      if (queued.has(service)) return "queued";
      if (!ctx.queueKnown) {
        unknownQueue.push(service);
        return "queue-unknown";
      }
      return null;
    });

    unknownQueue.forEach((service) => ctx.mirrorsKept.push(`offline-${service}`));
    sweep.failed.forEach(({ service, error }) =>
      ctx.failed.push({ store: `offline-${service}`, error })
    );
    if (!sweep.listed) {
      this.diagnostics.warn(
        "RootStore",
        "root.purge_orphan_mirrors",
        "purgeUserData: the device's offline storage could not be listed, so offline mirrors not owned by a store mounted in this session were NOT cleared",
        { data: { appName: this.appName } }
      );
    } else if (sweep.cleared.length) {
      this.diagnostics.info(
        "RootStore",
        "root.purge_orphan_mirrors",
        `purgeUserData: cleared ${sweep.cleared.length} offline mirror(s) no store mounted in this session owns`,
        { data: { services: sweep.cleared } }
      );
    }
    return sweep.cleared;
  }

  /**
   * Replay every offline store's queued writes NOW, while the session is still
   * signed in — bounded, never throws.
   *
   * The library AuthStore's logout calls `rootStore.flushOfflineQueue?.()`
   * before it ends the session, so work queued on a flaky connection lands
   * under its owner's identity instead of waiting on the device. This fork had
   * no such method, so the optional call no-oped: the writes were not lost (the
   * purge keeps any store holding them) but were never sent before sign-out.
   *
   * The library RootStore's `flushOfflineQueue` (31.3.x), over the same store
   * set purgeUserData clears. Stores still holding writes afterwards (offline, a
   * failed replay, the bound elapsing) are returned in `remaining`, and the
   * purge then keeps and reports them.
   *
   * It also drains "retired" stores, as the library does: a store that is
   * online-only now (`enableStorage: false`, or offline switched off) but holds
   * writes an earlier release queued for it. The library's ServiceStoreFactory
   * registers each against this RootStore, and `attemptRetiredOfflineStores`
   * runs their replay now; they are listed in `attempted` by service name, and
   * any write still queued for them afterwards is in `remaining`.
   *
   * @param options.timeoutMs Overall bound (default 5000 ms, the library's). A
   *   replay still running when it elapses is left to finish on its own.
   */
  async flushOfflineQueue(options: { timeoutMs?: number } = {}): Promise<{
    attempted: string[];
    remaining: Array<{ store: string; pendingCount: number }>;
  }> {
    const timeoutMs = options.timeoutMs ?? OFFLINE_LOGOUT_FLUSH_MS;
    const attempted: string[] = [];
    // The library calls this first thing in a sign-out: from here a sign-in or
    // refresh still in flight belongs to the person signing out (sessionGuard.ts).
    this.authEpoch += 1;
    try {
      await this.awaitOfflineQueueRestore();
      const pendingStores = () =>
        this.purgeableServiceStoreNames()
          .map((name) => [name, this.purgeableServiceStore(name)] as const)
          .filter(([, store]) => !!store?.hasPendingSync) as Array<
          [string, PurgeableServiceStore]
        >;

      const syncs: Promise<unknown>[] = [];
      for (const [name, store] of pendingStores()) {
        if (typeof store.syncWithServer !== "function" || store.isEffectivelyOffline) {
          continue;
        }
        attempted.push(name);
        syncs.push(
          Promise.resolve()
            .then(() => store.syncWithServer!())
            .catch((error: unknown) => {
              this.diagnostics.warn(
                "RootStore",
                "offline.sync_failed",
                `flushOfflineQueue: replay for ${name} failed; its writes stay queued`,
                { error, data: { store: name } }
              );
            })
        );
      }
      // Retired stores replay the writes an earlier release queued for them
      // through their own drain; run it now too. Never rejects.
      const retired = attemptRetiredOfflineStores(this);
      if (retired.names.length) {
        attempted.push(...retired.names);
        syncs.push(retired.settled);
      }
      if (syncs.length) {
        let timer: ReturnType<typeof setTimeout> | undefined;
        try {
          await Promise.race([
            Promise.allSettled(syncs),
            new Promise<void>((resolve) => {
              timer = setTimeout(resolve, timeoutMs);
            }),
          ]);
        } finally {
          if (timer) clearTimeout(timer);
        }
      }

      const pending = pendingStores();
      const remaining = [
        ...pending.map(([name, store]) => ({
          store: name,
          pendingCount: store.pendingSyncCount ?? 1,
        })),
        ...this.queuedOutside(
          new Set(pending.map(([name, store]) => String(store.name ?? name)))
        ),
      ];
      return { attempted, remaining };
    } catch (error) {
      // Never throws: sign-out must go ahead whatever the queue did.
      this.diagnostics.warn(
        "RootStore",
        "offline.sync_failed",
        "flushOfflineQueue: the pre-logout replay failed; queued writes are kept",
        { error }
      );
      return { attempted, remaining: [] };
    }
  }

  /** Service-tier keys plus the fork's built-in ServiceStore keys (see purgeUserData). */
  private purgeableServiceStoreNames(): string[] {
    return [
      ...new Set([
        ...this.storeRegistry.getServiceStoreNames(),
        ...builtInServiceStores,
      ]),
    ];
  }

  private purgeableServiceStore(name: string): PurgeableServiceStore | null {
    return (this.storeRegistry.getServiceStore(name) ??
      this.storeRegistry.getBuiltinStore(name)) as PurgeableServiceStore | null;
  }

  private queuedOperations(): QueuedOperation[] {
    const queue = this.offlineQueueManager as unknown as {
      getQueuedOperations?: () => QueuedOperation[];
    } | null;
    return typeof queue?.getQueuedOperations === "function"
      ? queue.getQueuedOperations()
      : [];
  }

  /**
   * Queued offline writes, counted per store, for stores whose service name is
   * NOT in `accounted` — purgeUserData's `stillQueued`. The library's
   * `queuedOutside`, keyed like the registry when the store is mounted.
   */
  private queuedOutside(
    accounted: Set<string>
  ): Array<{ store: string; pendingCount: number }> {
    const queue = this.offlineQueueManager as {
      getQueuedOperations?: () => Array<{ storeName: string }>;
    } | null;
    if (typeof queue?.getQueuedOperations !== "function") return [];
    const counts = new Map<string, number>();
    for (const op of queue.getQueuedOperations()) {
      if (!accounted.has(op.storeName)) {
        counts.set(op.storeName, (counts.get(op.storeName) ?? 0) + 1);
      }
    }
    if (!counts.size) return [];
    const keyOf = new Map<string, string>();
    for (const key of this.purgeableServiceStoreNames()) {
      const service = this.purgeableServiceStore(key)?.name;
      if (service && !keyOf.has(service)) keyOf.set(service, key);
    }
    return [...counts].map(([service, pendingCount]) => ({
      store: keyOf.get(service) ?? service,
      pendingCount,
    }));
  }

  /**
   * Wait, bounded, for the offline queue's restore from disk: `absent` (no
   * queue, nothing can be pending), `restored` / `failed`, or `timeout`.
   */
  private async awaitOfflineQueueRestore(
    timeoutMs = OFFLINE_QUEUE_RESTORE_WAIT_MS
  ): Promise<"absent" | "restored" | "failed" | "timeout"> {
    const queue = this.offlineQueueManager as {
      ready?: Promise<boolean>;
    } | null;
    if (!queue || !queue.ready) return "absent";
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        // ...and for the quarantine stamps the tracker starts on that same
        // `ready` (offlineReplay.ts), so nothing decides on a queue whose
        // owners are still being written.
        queue.ready.then(async (ok): Promise<"restored" | "failed"> => {
          if (!ok) return "failed";
          await this.offlineReplays().stampsSettled();
          return "restored";
        }),
        new Promise<"timeout">((resolve) => {
          timer = setTimeout(() => resolve("timeout"), timeoutMs);
        }),
      ]);
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  get serviceStoreChannels() {
    if (!this.serviceStores?.length) return [];
    return [...this.serviceStores, ...builtInServiceStores].reduce<string[]>(
      (servicesInChannels: string[], serviceStoreName: unknown): string[] => {
        const storeName = serviceStoreName as string;
        // Use StoreRegistry for type-safe access
        const serviceStore = this.storeRegistry.getServiceStore(storeName);
        if (serviceStore?.enableAutoChannel) {
          servicesInChannels.push(storeName);
        }
        const uniqueServicesInChannels = utils.uniqueArray(servicesInChannels);
        return uniqueServicesInChannels as string[];
      },
      []
    );
  }

  get currentChannelsServices() {
    if (!this.serviceStoreChannels?.length) return [];
    return this.serviceStoreChannels
      .filter((serviceStoreName: string) => {
        // Use StoreRegistry for type-safe access
        const serviceStore =
          this.storeRegistry.getServiceStore(serviceStoreName);
        return serviceStore?.name;
      })
      .map((serviceStoreName: string) => {
        const serviceStore =
          this.storeRegistry.getServiceStore(serviceStoreName);
        return serviceStore!.name;
      });
  }

  get anyServiceStoreLoading() {
    if (!this.serviceStores?.length) return false;
    return this.serviceStores.reduce<string[]>(
      (serviceStoreIsLoading, serviceStoreName) => {
        // Use StoreRegistry for type-safe access
        const serviceStore = this.storeRegistry.getServiceStore(
          serviceStoreName as string
        );
        if (serviceStore?.isServicePending) {
          serviceStoreIsLoading = [
            ...serviceStoreIsLoading,
            serviceStoreName as string,
          ];
        }
        return serviceStoreIsLoading;
      },
      []
    );
  }

  get anyServiceStoreHasError() {
    if (!this.serviceStores?.length) return false;
    return this.serviceStores.reduce(
      (serviceStoreHasError: unknown[], serviceStoreName: unknown) => {
        // Use StoreRegistry for type-safe access
        const serviceStore = this.storeRegistry.getServiceStore(
          serviceStoreName as string
        );
        if (serviceStore?.serviceHasError) {
          serviceStoreHasError = [...serviceStoreHasError, serviceStoreName];
        }
        return serviceStoreHasError;
      },
      []
    );
  }

  get storeCount() {
    return Object.keys(this).filter((key: string) => key.includes("Store"))
      .length;
  }

  get isConnected() {
    return this.connectionStore?.serverConnectionStatus;
  }

  get currentChannelServices() {
    return this.channelServices.slice();
  }

  get developmentUriOverride() {
    return this.apiOptions?.uriOverride?.development || "";
  }
}

export default RootStore;
